/**
 * Click Outside Handler Component for FlowRide Frontend
 * Wrapper component that detects clicks outside its children and triggers a callback.
 * Useful for closing dropdowns, modals, or other UI elements when clicking outside.
 */

import React, { type CSSProperties, useEffect, type ReactNode, type RefObject } from "react";

interface ClickOutsideHandlerProps {

    /** Child components to wrap */
    "children": ReactNode;

    /** Callback function triggered when clicking outside */
    "onOutsideClick": () => void;

    /** CSS class name for the wrapper div */
    "className": string;

    /** Reference to the wrapper div element */
    "wrapperRef": RefObject<HTMLDivElement>;

    /** Optional inline styles for the wrapper div */
    "style"?: CSSProperties;
}

/**
 * ClickOutsideHandler component that detects clicks outside its children
 * @param props - Component props
 * @returns JSX element with click outside detection
 */
const ClickOutsideHandler: React.FC<ClickOutsideHandlerProps> = ( { children, onOutsideClick, className, wrapperRef, style } ) => {
    useEffect( () => {
    /**
     * Handle click outside event
     * @param event - Mouse event
     */
        const handleClickOutside = ( event: MouseEvent ) => {
            if ( wrapperRef.current != null && !wrapperRef.current.contains( event.target as Node ) ) {
                onOutsideClick();
            }
        };

        // Bind the event listener
        document.addEventListener( "mousedown", handleClickOutside );

        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener( "mousedown", handleClickOutside );
        };
    }, [ onOutsideClick, wrapperRef ] );

    return (
        <div
            className={className}
            ref={wrapperRef}
            style={style}
        >
            {children}
        </div>
    );
};

export default ClickOutsideHandler;
